import React from 'react';
import { NavLink } from 'react-router-dom';
import { BarChart3, TrendingUp, Target, ChevronLeft, ChevronRight, FolderOpen } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

interface SidebarProps {
  className?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ className = '', isCollapsed = false, onToggleCollapse }) => {
  const navItems = [
    {
      path: '/dashboard',
      icon: BarChart3,
      label: 'Sales Stages',
      description: 'Pipeline analysis and scoring'
    },
    {
      path: '/trends',
      icon: TrendingUp,
      label: 'Trends',
      description: 'Performance trends over time'
    },
    {
      path: '/insights',
      icon: Target,
      label: 'Insights',
      description: 'Win rate optimization recommendations'
    },
    {
      path: '/open-cycles',
      icon: FolderOpen,
      label: 'Open Cycles',
      description: 'Sales cycle activity tracking'
    }
  ];

  return (
    <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    } ${className}`}>
      <div className="flex flex-col h-full">
        {/* Logo/Brand with Collapse Button */}
        <div className={`flex items-center border-b border-gray-200 dark:border-gray-700 ${
          isCollapsed ? 'px-3 py-4 justify-between flex-col space-y-3' : 'px-6 py-4 justify-between'
        }`}>
          {isCollapsed ? (
            <>
              <div className="flex flex-col items-center space-y-1">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs font-bold text-purple-600 dark:text-purple-400 font-plus-jakarta">AI</div>
              </div>
              <button
                onClick={onToggleCollapse}
                className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 transition-all duration-200"
                title="Expand sidebar"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales AI</h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-inter">Analytics Dashboard</p>
                </div>
              </div>
              <button
                onClick={onToggleCollapse}
                className="flex items-center justify-center w-7 h-7 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 transition-all duration-200"
                title="Collapse sidebar"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex transition-all duration-200 group relative ${
                        isCollapsed 
                          ? 'flex-col items-center px-2 py-3 rounded-lg mx-1' 
                          : 'items-center px-3 py-3 rounded-lg'
                      } ${
                        isActive
                          ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                      }`
                    }
                    title={isCollapsed ? `${item.label} - ${item.description}` : undefined}
                  >
                    <IconComponent 
                      className={`transition-colors ${
                        isCollapsed
                          ? 'w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'
                          : 'w-5 h-5 mr-3 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                      }`} 
                    />
                    {isCollapsed ? (
                      <span className="text-xs font-medium font-inter text-gray-600 dark:text-gray-400 mt-1 text-center leading-tight">
                        {item.label.split(' ').map((word, index) => (
                          <div key={index}>{word}</div>
                        ))}
                      </span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium font-inter">{item.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-inter">{item.description}</span>
                      </div>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Theme Toggle */}
        <div className={`px-4 py-3 border-t border-gray-200 dark:border-gray-700`}>
          <ThemeToggle isCollapsed={isCollapsed} />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;